import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import MCStatCard from "@/components/master_control/MCStatCard";
import { MCUsersTable } from "@/components/master_control/MCUsersTable";
import { MCOrgsTable } from "@/components/master_control/MCOrgsTable";
import MCAgentUsageCard from "@/components/master_control/MCAgentUsageCard";
import MCSystemHealth from "@/components/master_control/MCSystemHealth";
import MCErrorLog from "@/components/master_control/MCErrorLog";
import MCDangerZone from "@/components/master_control/MCDangerZone";
import MCAuditLogTable from "@/components/master_control/MCAuditLogTable";
import MCWorkflowInspector from "@/components/master_control/MCWorkflowInspector";
import MCIngestionDashboard from "@/components/master_control/MCIngestionDashboard";
import MCSettingsPanel from "@/components/master_control/MCSettingsPanel";
import MCAlertsPanel from "@/components/master_control/MCAlertsPanel";
import MCPerformanceIndicators from "@/components/master_control/MCPerformanceIndicators";
import MCExportTools from "@/components/master_control/MCExportTools";
import MCPromptAtlas from "@/components/master_control/MCPromptAtlas";
import { request } from "@/api/client";
import { fetchFrontendErrorSummary, type CapabilityDebugPayload, type FrontendErrorSummary } from "@/api/executive";
import { useToast } from "@/components/ToastProvider";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useSessionUser } from "@/hooks/useSessionUser";
import { useMasterControl } from "@/hooks/useMasterControl";
import type { MasterControlDangerActions, MasterControlDryRun } from "@/hooks/useMasterControl";
import { Link, useLocation } from "react-router-dom";
import { severityStyles } from "@/observability/frontendTypes";
import { AutopilotPanel } from "@/pages/MasterControl/AutopilotPanel";
import { normalizePlanKey, planKeyToBackendTier, planLabel, type PlanKeyExt } from "@/lib/plans";

const AGENT_CONFIG = [
  { key: "booking", label: "Booking Agent", aliases: ["booking_agent"] },
  { key: "publicist", label: "Publicist Agent", aliases: ["publicist_agent"] },
  { key: "marketing", label: "Marketing Agent", aliases: ["marketing_agent"] },
  { key: "manager", label: "Manager Agent", aliases: ["manager_agent"] },
  { key: "accounting", label: "Accounting Agent", aliases: ["accountant", "accountant_agent"] },
  { key: "routing", label: "Routing Agent", aliases: ["router", "routing_agent"] },
] as const;
const OWNER_TIER_OPTIONS = ["STANDARD", "PRO", "AGENCY", "OWNER"] as const;
const PLAN_DEBUG_FLAGS = ["plan.plus", "autopilot_view", "observability.frontend"] as const;

function resolveSystemTone(status?: string | null) {
  const normalized = (status ?? "").toString().toLowerCase();
  if (normalized.includes("error") || normalized.includes("fail") || normalized.includes("down")) return "danger";
  if (normalized.includes("warn") || normalized.includes("degraded")) return "warning";
  if (normalized.includes("ok") || normalized.includes("healthy")) return "success";
  return "default";
}

function formatBytes(value?: number | null): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const precision = unitIndex === 0 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(precision)} ${units[unitIndex]}`;
}

function formatNumber(value?: number | null): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US");
}

function formatCost(value?: number | null): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `$${value.toFixed(2)}`;
}

export default function MasterControl() {
  const {
    users,
    organizations,
    agentUsage,
    aiUsage,
    metrics,
    health,
    errors,
    auditLogs,
    workflowState,
    ingestionState,
    performanceState,
    loading,
    error,
    refresh,
    derived,
    dangerActions,
    dryRun,
    fetchWorkflowState,
    fetchIngestionState,
    fetchPerformanceState,
    runGlobalSync,
    settingsState,
    fetchSettings,
    saveSettings,
    alertSettings,
    fetchAlertSettings,
    saveAlertSettings,
    sendTestAlert,
    eventsConnected,
    capabilityProfile,
    promptAtlas,
    fetchPromptAtlas,
    updatePromptAtlasEntry,
    resetPromptAtlasEntry,
  } = useMasterControl();
  const { capabilities, openUpgradeModal } = useCapabilities();
  const { sessionUser, refreshSession } = useSessionUser();
  const { push } = useToast();
  const location = useLocation();
  const normalizedSessionPlan = normalizePlanKey(sessionUser?.tier ?? sessionUser?.role);
  const [tierSelection, setTierSelection] = useState<PlanKeyExt>(normalizedSessionPlan);
  const [tierUpdating, setTierUpdating] = useState(false);
  const [frontendSummary, setFrontendSummary] = useState<FrontendErrorSummary | null>(null);
  const [frontendSummaryLoading, setFrontendSummaryLoading] = useState(false);
  const isInvitesActive = location.pathname.startsWith("/control/master/invites");
  const isRegistryActive = location.pathname.startsWith("/control/master/registry");
  const isOverviewActive = !isInvitesActive && !isRegistryActive;

  useEffect(() => {
    setTierSelection(normalizedSessionPlan);
  }, [normalizedSessionPlan]);

  const hasCapability = useCallback(
    (flag: string | undefined) => {
      if (!flag) return true;
      return Boolean((capabilities as Record<string, unknown> | undefined)?.[flag]);
    },
    [capabilities],
  );
  const canViewFrontendErrors = hasCapability("observability.frontend");
  const canManageAutopilot = hasCapability("autopilot_manage");
  const masterControlGateAllowed = hasCapability("canViewMasterControl");
  const planCardReasons = useMemo<string[]>(() => {
    const reasons: string[] = [];
    if (!sessionUser) {
      reasons.push("Session user has not loaded yet.");
      return reasons;
    }
    if (!sessionUser.role) {
      reasons.push("Session role is missing.");
    }
    if (normalizedSessionPlan !== "OWNER") {
      reasons.push(`Normalized plan key is ${normalizedSessionPlan}, owner required.`);
    }
    if (!masterControlGateAllowed) {
      reasons.push("Client capability gate 'canViewMasterControl' failed.");
    }
    return reasons;
  }, [sessionUser, normalizedSessionPlan, masterControlGateAllowed]);
  const planCardVisible = planCardReasons.length === 0;
  const clientCapabilityStates = PLAN_DEBUG_FLAGS.map((flag) => ({
    flag,
    allowed: hasCapability(flag),
  }));
  const backendCapabilityStates = PLAN_DEBUG_FLAGS.map((flag) => ({
    flag,
    allowed: capabilityProfile ? Boolean(capabilityProfile.capabilities?.[flag]) : null,
  }));

  useEffect(() => {
    if (!canViewFrontendErrors) {
      setFrontendSummary(null);
      return;
    }
    setFrontendSummaryLoading(true);
    fetchFrontendErrorSummary()
      .then((value) => setFrontendSummary(value))
      .catch(() => setFrontendSummary(null))
      .finally(() => setFrontendSummaryLoading(false));
  }, [canViewFrontendErrors]);

  const guardCapability = useCallback(
    async <T,>(flag: string, action: () => Promise<T> | T, featureLabel?: string): Promise<T | undefined> => {
      if (!hasCapability(flag)) {
        openUpgradeModal(featureLabel ?? flag);
        return undefined;
      }
      try {
        return await action();
      } catch (err) {
        if (err && typeof err === "object" && "error" in err) {
          const code = (err as { error?: string }).error;
          if (code === "requires_pro" || code === "requires_owner") {
            openUpgradeModal(code === "requires_owner" ? "Owner tier" : "Pro tier");
            return undefined;
          }
        }
        throw err;
      }
    },
    [hasCapability, openUpgradeModal],
  );

  const totalAgentActions24h = useMemo(() => {
    if (typeof metrics?.agent_actions_24h === "number") return metrics.agent_actions_24h;
    if (typeof metrics?.agent_actions_last_24h === "number") return metrics.agent_actions_last_24h;
    return Object.values(agentUsage).reduce((sum, entry) => sum + (entry.last_24h_runs ?? (entry as any).last24h ?? 0), 0);
  }, [agentUsage, metrics]);

  const systemStatusLabel = health.core?.status ?? "Unknown";
  const statCards = [
    {
      label: "Total users",
      value: metrics?.total_users ?? users.length,
      hint: "Across every org",
    },
    {
      label: "Total organizations",
      value: metrics?.total_organizations ?? organizations.length,
      hint: "Active + paused",
    },
    {
      label: "Daily active users",
      value: derived.dau,
      hint: "Past 24h",
    },
    {
      label: "Monthly active users",
      value: derived.mau,
      hint: "Past 30d",
    },
    {
      label: "Agent actions (24h)",
      value: totalAgentActions24h,
      hint: "All automations",
    },
    {
      label: "System health",
      value: systemStatusLabel,
      hint: eventsConnected ? "SSE connected" : "SSE reconnecting…",
      tone: resolveSystemTone(systemStatusLabel),
    },
    {
      label: "Revenue (Coming soon)",
      value: metrics?.revenue_placeholder ?? "—",
      hint: "Coming soon",
    },
  ];

  const handleRefresh = useCallback(() => guardCapability("canViewMasterControl", () => refresh(), "Master Control"), [
    guardCapability,
    refresh,
  ]);

  const handleWorkflowRefresh = useCallback(
    () => guardCapability("canViewWorkflowInspector", () => fetchWorkflowState(), "Workflow inspector"),
    [fetchWorkflowState, guardCapability],
  );

  const handleIngestionRefresh = useCallback(
    () => guardCapability("canTriggerIngestionJobs", () => fetchIngestionState(), "Ingestion dashboard"),
    [fetchIngestionState, guardCapability],
  );

  const handleRunGlobalSync = useCallback(
    () => guardCapability("canTriggerIngestionJobs", () => runGlobalSync(), "Ingestion dashboard"),
    [guardCapability, runGlobalSync],
  );

  const handlePerformanceRefresh = useCallback(
    () => guardCapability("canViewSystemHealth", () => fetchPerformanceState(), "System health"),
    [fetchPerformanceState, guardCapability],
  );

  const handleSettingsSave = useCallback(
    (next: Parameters<typeof saveSettings>[0]) =>
      guardCapability("canModifyGlobalSettings", () => saveSettings(next), "Global settings"),
    [guardCapability, saveSettings],
  );

  const handleSettingsReload = useCallback(
    () => guardCapability("canModifyGlobalSettings", () => fetchSettings(), "Global settings"),
    [fetchSettings, guardCapability],
  );

  const handleAlertSave = useCallback(
    (next: Parameters<typeof saveAlertSettings>[0]) =>
      guardCapability("canModifyGlobalSettings", () => saveAlertSettings(next), "Alerting"),
    [guardCapability, saveAlertSettings],
  );

  const handleAlertReload = useCallback(
    () => guardCapability("canModifyGlobalSettings", () => fetchAlertSettings(), "Alerting"),
    [fetchAlertSettings, guardCapability],
  );

  const handleTestAlert = useCallback(
    () => guardCapability("canModifyGlobalSettings", () => sendTestAlert(), "Alerting"),
    [guardCapability, sendTestAlert],
  );

  const handlePromptAtlasRefresh = useCallback(async () => {
    await guardCapability("canViewMasterControl", () => fetchPromptAtlas(), "Prompt atlas");
  }, [fetchPromptAtlas, guardCapability]);

  const handlePromptSave = useCallback(
    async (promptId: string, prompt: string) => {
      await guardCapability("canModifyGlobalSettings", () => updatePromptAtlasEntry(promptId, prompt), "Prompt atlas");
    },
    [guardCapability, updatePromptAtlasEntry],
  );

  const handlePromptReset = useCallback(
    async (promptId: string) => {
      await guardCapability("canModifyGlobalSettings", () => resetPromptAtlasEntry(promptId), "Prompt atlas");
    },
    [guardCapability, resetPromptAtlasEntry],
  );

  const secureDangerActions = useMemo<MasterControlDangerActions>(
    () => ({
      deleteUser: (userId: string) =>
        guardCapability("canRunDangerousActions", () => dangerActions.deleteUser(userId), "Danger zone"),
      deleteOrganization: (orgId: string) =>
        guardCapability("canRunDangerousActions", () => dangerActions.deleteOrganization(orgId), "Danger zone"),
      clearRedisQueues: () =>
        guardCapability("canRunDangerousActions", () => dangerActions.clearRedisQueues(), "Danger zone"),
      purgeAgentMemory: (agentId: string) =>
        guardCapability("canRunDangerousActions", () => dangerActions.purgeAgentMemory(agentId), "Danger zone"),
      resetWorkflowEngine: () =>
        guardCapability("canRunDangerousActions", () => dangerActions.resetWorkflowEngine(), "Danger zone"),
      triggerGlobalResync: () =>
        guardCapability("canRunDangerousActions", () => dangerActions.triggerGlobalResync(), "Danger zone"),
    }),
    [dangerActions, guardCapability],
  );

  const secureDryRun = useMemo<MasterControlDryRun>(() => {
    return {
      userDeletion: (userId: string) =>
        guardCapability("canRunDangerousActions", () => dryRun.userDeletion(userId), "Danger zone"),
      organizationDeletion: (orgId: string) =>
        guardCapability("canRunDangerousActions", () => dryRun.organizationDeletion(orgId), "Danger zone"),
      redis: () => guardCapability("canRunDangerousActions", () => dryRun.redis(), "Danger zone"),
      chroma: (agentId: string) =>
        guardCapability("canRunDangerousActions", () => dryRun.chroma(agentId), "Danger zone"),
      workflows: () => guardCapability("canRunDangerousActions", () => dryRun.workflows(), "Danger zone"),
      sync: () => guardCapability("canTriggerIngestionJobs", () => dryRun.sync(), "Ingestion dashboard"),
    };
  }, [dryRun, guardCapability]);

  const handleTierApply = useCallback(async () => {
    if (!sessionUser) return;
    setTierUpdating(true);
    const payloadUserId = sessionUser.user?.id;
    if (!payloadUserId) {
      throw new Error("Session identity missing user id");
    }
    try {
      await request("/master-control/set-tier", {
        method: "POST",
        body: { user_id: payloadUserId, tier: planKeyToBackendTier(tierSelection) },
      });
      push({ title: "Plan updated", message: `Session plan set to ${planLabel(tierSelection)}` });
      if (refreshSession) {
        await refreshSession();
      } else {
        window.location.reload();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to update plan";
      push({ title: "Plan update failed", message });
    } finally {
      setTierUpdating(false);
    }
  }, [push, refreshSession, sessionUser, tierSelection]);
  const canEditPrompts = hasCapability("canModifyGlobalSettings");
  const canResetWorkflows = hasCapability("canRunDangerousActions");
  const canExportAny = useMemo(() => hasCapability("canViewMasterControl"), [hasCapability]);
  const sectionAnchors = useMemo(
    () => [
      { id: "mc-overview", label: "Overview" },
      { id: "mc-data-ops", label: "Data Ops" },
      { id: "mc-usage", label: "Usage" },
      { id: "mc-health", label: "Health" },
      { id: "mc-automation", label: "Automation" },
      { id: "mc-config", label: "Configuration" },
      { id: "mc-audit", label: "Audit" },
      { id: "mc-danger", label: "Danger" },
    ],
    [],
  );
  const [activeSection, setActiveSection] = useState(sectionAnchors[0]?.id ?? "mc-overview");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: [0, 0.2, 0.6] },
    );
    const nodes = sectionAnchors
      .map((anchor) => document.getElementById(anchor.id))
      .filter((node): node is HTMLElement => Boolean(node));
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sectionAnchors]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || (target as HTMLElement | null)?.isContentEditable) {
        return;
      }
      const key = event.key;
      const index = Number.parseInt(key, 10);
      if (!Number.isFinite(index) || index < 1 || index > sectionAnchors.length) {
        return;
      }
      const anchor = sectionAnchors[index - 1];
      if (!anchor) return;
      const node = document.getElementById(anchor.id);
      if (node) {
        event.preventDefault();
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [sectionAnchors]);

  return (
    <SectionGate flag="canViewMasterControl" fallback={<LockedFeatureCard flag="canViewMasterControl" />}>
      <div className="aa-page-shell space-y-8">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-primary">Admin</p>
            <h1 className="aa-page-title text-3xl font-semibold">Admin Settings</h1>
            <p className="text-sm text-muted-foreground">
              System analytics, health monitoring, and administrative settings for your organization.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-primary/50 bg-primary/10 text-foreground hover:bg-primary/20"
              disabled={loading}
              onClick={() => {
                void handleRefresh();
              }}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </header>
        <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.3em] text-muted-foreground">
          <Link
            to="/control/master"
            className={
              isOverviewActive
                ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
                : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
            }
          >
            Overview
          </Link>
          <Link
            to="/control/master/invites"
            className={
              isInvitesActive
                ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
                : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
            }
          >
            Invites
          </Link>
          <Link
            to="/control/master/registry"
            className={
              isRegistryActive
                ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
                : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
            }
          >
            Registry
          </Link>
        </div>
        {error && <p className="text-sm text-rose-300">{error}</p>}
        {import.meta.env.DEV && (
          <p className="text-xs text-muted-foreground">
            Debug: role={sessionUser?.role ?? "—"} | tier={sessionUser?.tier ?? "—"} | plan={planLabel(
              sessionUser?.tier,
            )}
          </p>
        )}

        <div className="sticky top-3 z-20 flex flex-wrap gap-2 rounded-full border border-border/40 bg-background/70 px-3 py-2 text-[11px] uppercase tracking-[0.3em] text-muted-foreground shadow-sm backdrop-blur">
          {sectionAnchors.map((anchor) => {
            const isActive = activeSection === anchor.id;
            return (
              <a
                key={anchor.id}
                href={`#${anchor.id}`}
                className={
                  isActive
                    ? "rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground transition hover:bg-primary/20"
                    : "rounded-full border border-transparent px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70 transition hover:border-border/40 hover:text-foreground"
                }
                aria-current={isActive ? "true" : undefined}
              >
                {anchor.label}
              </a>
            );
          })}
          <span className="ml-auto hidden text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70 sm:inline">
            Alt + 1–{sectionAnchors.length}
          </span>
        </div>

        <section id="mc-overview" className="scroll-mt-24 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Overview</h2>
            <p className="text-sm text-muted-foreground">High-level visibility and account diagnostics.</p>
          </div>
          <Accordion type="single" collapsible defaultValue="diagnostics">
            <AccordionItem value="diagnostics" className="rounded-2xl border border-border bg-background/40">
              <AccordionTrigger className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">
                Diagnostics
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-4">
                <PlanTestingDebugPanel
                  sessionRole={sessionUser?.role}
                  sessionTier={sessionUser?.tier ?? sessionUser?.role ?? null}
                  normalizedSessionPlan={normalizedSessionPlan}
                  capabilityProfile={capabilityProfile}
                  planCardVisible={planCardVisible}
                  planCardReasons={planCardReasons}
                  clientCapabilityStates={clientCapabilityStates}
                  backendCapabilityStates={backendCapabilityStates}
                  masterControlGateAllowed={masterControlGateAllowed}
                />
                {planCardVisible ? (
                  <OwnerTierToggleCard
                    value={tierSelection}
                    onValueChange={setTierSelection}
                    onApply={() => {
                      void handleTierApply();
                    }}
                    applying={tierUpdating}
                    currentTier={normalizedSessionPlan}
                  />
                ) : (
                  <PlanTestingPlaceholder reasons={planCardReasons} />
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <SectionGate flag="canViewSystemStats" fallback={<LockedFeatureCard flag="canViewSystemStats" />}>
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {statCards.map((stat) => (
                <MCStatCard key={stat.label} {...stat} />
              ))}
            </section>
          </SectionGate>

          <MCCapabilityProfile profile={capabilityProfile} />

          <section className="space-y-4">
            <MCUsersTable users={users} loading={loading} />
            <MCOrgsTable organizations={organizations} loading={loading} />
          </section>

        </section>

        <section id="mc-data-ops" className="scroll-mt-24 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Data Ops</h2>
            <p className="text-sm text-muted-foreground">Exports and data snapshots for audits.</p>
          </div>
          {canExportAny ? (
            <MCExportTools />
          ) : (
            <LockedFeatureCard
              flag="canViewSystemStats"
              title="Exports locked"
              description="Upgrade to access system exports and audit data pulls."
            />
          )}
        </section>

        <section id="mc-usage" className="scroll-mt-24 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Usage</h2>
            <p className="text-sm text-muted-foreground">Automation volume and AI spend visibility.</p>
          </div>
          <SectionGate flag="canViewAgentUsage" fallback={<LockedFeatureCard flag="canViewAgentUsage" />}>
            <section className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Agent usage summary</h3>
                <p className="text-sm text-muted-foreground">Six primary agents aggregated from /executive/agent-usage.</p>
              </div>
              {!loading && Object.keys(agentUsage).length === 0 ? (
                <p className="text-sm text-muted-foreground">No agent usage recorded yet.</p>
              ) : null}
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {AGENT_CONFIG.map((agent) => {
                  const usage =
                    agentUsage[agent.key] ||
                    agent.aliases.map((alias) => agentUsage[alias]).find(Boolean) ||
                    undefined;
                  return <MCAgentUsageCard key={agent.key} title={agent.label} usage={usage} />;
                })}
              </div>
            </section>
          </SectionGate>

          <SectionGate flag="canViewSystemStats" fallback={<LockedFeatureCard flag="canViewSystemStats" />}>
            <section className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">AI usage</h3>
                <p className="text-sm text-muted-foreground">Last {aiUsage?.window_days ?? 30} days of token usage.</p>
              </div>
              {!loading && !aiUsage ? (
                <p className="text-sm text-muted-foreground">No AI usage data reported yet.</p>
              ) : null}
              <div className="grid gap-4 lg:grid-cols-3">
                <Card className="rounded-2xl border border-border bg-background/60">
                  <CardHeader className="p-0">
                    <CardTitle className="text-base font-semibold text-foreground">Totals</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 pt-3 text-sm text-muted-foreground">
                    <div className="flex items-center justify-between">
                      <span>Requests</span>
                      <span className="font-semibold text-foreground">
                        {formatNumber(aiUsage?.totals?.requests ?? 0)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span>Total tokens</span>
                      <span className="font-semibold text-foreground">
                        {formatNumber(aiUsage?.totals?.total_tokens ?? 0)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span>Cost</span>
                      <span className="font-semibold text-foreground">{formatCost(aiUsage?.totals?.cost_usd ?? 0)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border border-border bg-background/60">
                  <CardHeader className="p-0">
                    <CardTitle className="text-base font-semibold text-foreground">Top orgs</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 pt-3 text-xs text-muted-foreground">
                    <div className="space-y-2">
                      {(aiUsage?.orgs ?? []).slice(0, 6).map((entry) => (
                        <div key={entry.org_id ?? entry.org_name ?? "org"} className="flex items-center justify-between">
                          <span className="truncate pr-2">{entry.org_name ?? entry.org_id ?? "Unknown"}</span>
                          <span className="font-semibold text-foreground">{formatCost(entry.cost_usd)}</span>
                        </div>
                      ))}
                      {(!aiUsage?.orgs || aiUsage.orgs.length === 0) && <div>No usage recorded yet.</div>}
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border border-border bg-background/60">
                  <CardHeader className="p-0">
                    <CardTitle className="text-base font-semibold text-foreground">Top users</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 pt-3 text-xs text-muted-foreground">
                    <div className="space-y-2">
                      {(aiUsage?.users ?? []).slice(0, 6).map((entry) => (
                        <div
                          key={entry.user_id ?? entry.user_email ?? "user"}
                          className="flex items-center justify-between"
                        >
                          <span className="truncate pr-2">{entry.user_email ?? entry.user_id ?? "Unknown"}</span>
                          <span className="font-semibold text-foreground">{formatCost(entry.cost_usd)}</span>
                        </div>
                      ))}
                      {(!aiUsage?.users || aiUsage.users.length === 0) && <div>No usage recorded yet.</div>}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>
          </SectionGate>
        </section>

        <section id="mc-health" className="scroll-mt-24 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Health</h2>
            <p className="text-sm text-muted-foreground">Core infrastructure health and client telemetry.</p>
          </div>
          <SectionGate flag="canViewSystemHealth" fallback={<LockedFeatureCard flag="canViewSystemHealth" />}>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-border bg-background/60 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">VPN</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {String((health.core as Record<string, any> | null)?.system?.vpn?.status ?? "unknown")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {(health.core as Record<string, any> | null)?.system?.vpn?.interface
                    ? `iface ${(health.core as Record<string, any> | null)?.system?.vpn?.interface}`
                    : "interface not detected"}
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-background/60 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Memory</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {formatBytes((health.core as Record<string, any> | null)?.system?.memory?.used_bytes)} used
                </p>
                <p className="text-xs text-muted-foreground">
                  {typeof (health.core as Record<string, any> | null)?.system?.memory?.percent_used === "number"
                    ? `${(health.core as Record<string, any> | null)?.system?.memory?.percent_used}% used`
                    : "—"}
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-background/60 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Swap</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {formatBytes((health.core as Record<string, any> | null)?.system?.swap?.used_bytes)} used
                </p>
                <p className="text-xs text-muted-foreground">
                  {typeof (health.core as Record<string, any> | null)?.system?.swap?.percent_used === "number"
                    ? `${(health.core as Record<string, any> | null)?.system?.swap?.percent_used}% used`
                    : "—"}
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-background/60 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Storage</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {formatBytes(((health.core as Record<string, any> | null)?.system?.storage?.[0] as any)?.used_bytes)} used
                </p>
                <p className="text-xs text-muted-foreground">
                  {typeof ((health.core as Record<string, any> | null)?.system?.storage?.[0] as any)?.percent_used ===
                  "number"
                    ? `${((health.core as Record<string, any> | null)?.system?.storage?.[0] as any)?.percent_used}% used`
                    : "—"}
                </p>
              </div>
            </div>
          </SectionGate>

          <div className="grid gap-4 lg:grid-cols-2">
            <SectionGate
              flag="canViewSystemHealth"
              fallback={<LockedFeatureCard flag="canViewSystemHealth" className="h-full" />}
            >
              <Tabs defaultValue="system" className="w-full">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Infrastructure</p>
                    <p className="text-sm text-muted-foreground">Switch between runtime health and performance.</p>
                  </div>
                  <TabsList className="h-9">
                    <TabsTrigger value="system">System</TabsTrigger>
                    <TabsTrigger value="performance">Performance</TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="system">
                  <MCSystemHealth health={health} />
                </TabsContent>
                <TabsContent value="performance">
                  <MCPerformanceIndicators
                    performance={performanceState}
                    loading={loading && !performanceState}
                    onRefresh={handlePerformanceRefresh}
                  />
                </TabsContent>
              </Tabs>
            </SectionGate>
          </div>

          <SectionGate
            flag="observability.frontend"
            fallback={
              <LockedFeatureCard
                flag="observability.frontend"
                title="Frontend health locked"
                description="Upgrade to monitor client-side crash volume, affected users, and build regressions."
              />
            }
          >
            <MCFrontendHealthPanel summary={frontendSummary} loading={frontendSummaryLoading} />
          </SectionGate>
        </section>

        <section id="mc-automation" className="scroll-mt-24 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Automation</h2>
            <p className="text-sm text-muted-foreground">Workflow orchestration and ingestion pipelines.</p>
          </div>
          <SectionGate flag="autopilot_view" fallback={<LockedFeatureCard flag="autopilot_view" />}>
            <section className="space-y-4">
              <AutopilotPanel canManage={canManageAutopilot} />
            </section>
          </SectionGate>

          <SectionGate flag="canViewWorkflowInspector" fallback={<LockedFeatureCard flag="canViewWorkflowInspector" />}>
            <MCWorkflowInspector
              state={workflowState}
              loading={loading && !workflowState}
              onRefresh={handleWorkflowRefresh}
              dangerActions={secureDangerActions}
              canReset={canResetWorkflows}
            />
          </SectionGate>

          <SectionGate flag="canTriggerIngestionJobs" fallback={<LockedFeatureCard flag="canTriggerIngestionJobs" />}>
            <MCIngestionDashboard
              pipelines={ingestionState}
              loading={loading && !ingestionState}
              onRefresh={handleIngestionRefresh}
              onRunFull={handleRunGlobalSync}
              dryRunSync={secureDryRun.sync}
              canRunSync={hasCapability("canTriggerIngestionJobs")}
              canDryRun={hasCapability("canTriggerIngestionJobs")}
            />
          </SectionGate>
        </section>

        <section id="mc-config" className="scroll-mt-24 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Configuration</h2>
            <p className="text-sm text-muted-foreground">Global settings, prompts, and alert routing.</p>
          </div>
          <SectionGate flag="canModifyGlobalSettings" fallback={<LockedFeatureCard flag="canModifyGlobalSettings" />}>
            <MCSettingsPanel settings={settingsState} onSave={handleSettingsSave} onReload={handleSettingsReload} />
          </SectionGate>

          <SectionGate flag="canModifyGlobalSettings" fallback={<LockedFeatureCard flag="canModifyGlobalSettings" />}>
            <MCPromptAtlas
              entries={promptAtlas}
              loading={loading && promptAtlas.length === 0}
              onRefresh={handlePromptAtlasRefresh}
              onEdit={handlePromptSave}
              onReset={handlePromptReset}
              canEdit={canEditPrompts}
            />
          </SectionGate>

          <SectionGate flag="canModifyGlobalSettings" fallback={<LockedFeatureCard flag="canModifyGlobalSettings" />}>
            <MCAlertsPanel
              settings={alertSettings}
              onSave={handleAlertSave}
              onTest={handleTestAlert}
              onReload={handleAlertReload}
            />
          </SectionGate>
        </section>

        <section id="mc-audit" className="scroll-mt-24 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Audit</h2>
            <p className="text-sm text-muted-foreground">Administrative history and change tracking.</p>
          </div>
          <Accordion type="single" collapsible>
            <AccordionItem value="audit" className="rounded-2xl border border-border bg-background/40">
              <AccordionTrigger className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">
                Audit log
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <SectionGate flag="canViewAuditLog" fallback={<LockedFeatureCard flag="canViewAuditLog" />}>
                  <MCAuditLogTable logs={auditLogs} />
                </SectionGate>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        <section id="mc-danger" className="scroll-mt-24 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Danger zone</h2>
            <p className="text-sm text-muted-foreground">Irreversible administrative actions.</p>
          </div>
          <Accordion type="single" collapsible>
            <AccordionItem value="danger" className="rounded-2xl border border-border bg-background/40">
              <AccordionTrigger className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">
                Dangerous actions
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <SectionGate flag="canRunDangerousActions" fallback={<LockedFeatureCard flag="canRunDangerousActions" />}>
                  <MCDangerZone
                    actions={secureDangerActions}
                    dryRun={secureDryRun}
                    onActionComplete={() => {
                      void handleRefresh();
                    }}
                  />
                </SectionGate>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
    </div>
    </SectionGate>
  );
}

function SectionGate({
  flag,
  fallback,
  children,
}: {
  flag: string;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { capabilities, hasFullAccess, ready } = useCapabilities();
  if (!ready) {
    return (
      <div className="rounded-2xl border border-border bg-background/40 p-4 text-sm text-muted-foreground">
        <p>Loading entitlements…</p>
      </div>
    );
  }
  const allowed = hasFullAccess || Boolean((capabilities as Record<string, unknown> | undefined)?.[flag]);
  if (allowed) {
    return <>{children}</>;
  }
  return <>{fallback ?? <LockedFeatureCard flag={flag} />}</>;
}

function MCFrontendHealthPanel({ summary, loading }: { summary: FrontendErrorSummary | null; loading: boolean }) {
  const severityEntries = Object.entries(summary?.severity ?? {}).sort((a, b) => b[1] - a[1]);
  const versionEntries = Object.entries(summary?.versions ?? {}).sort((a, b) => b[1] - a[1]);
  const severityTotal = severityEntries.reduce((sum, [, count]) => sum + count, 0) || 1;

  return (
    <div className="aa-glass-card rounded-2xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Frontend health</p>
          <h2 className="text-xl font-semibold text-foreground">Client error telemetry</h2>
        </div>
        <div className="flex gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to="/observability/frontend">View logs</Link>
          </Button>
          <Button asChild size="sm">
            <Link to="/observability/frontend/live">Live stream</Link>
          </Button>
        </div>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : summary ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Errors (24h)</p>
              <p className="text-2xl font-semibold text-foreground">{summary.last24h}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Affected users</p>
              <p className="text-2xl font-semibold text-foreground">{summary.uniqueUsers}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Severity</p>
              <div className="mt-2 space-y-3">
                {severityEntries.length === 0 && <p className="text-sm text-muted-foreground">No errors logged.</p>}
                {severityEntries.map(([severity, count]) => (
                  <div key={severity}>
                    <div className="flex items-center justify-between text-xs text-foreground/70">
                      <span className="capitalize">{severity}</span>
                      <span>{count}</span>
                    </div>
                    <div className="mt-1 h-2 rounded bg-background/5">
                      <div
                        className={`h-2 rounded ${severityStyles[severity as keyof typeof severityStyles] ?? "bg-primary/70"}`}
                        style={{ width: `${Math.min(100, Math.round((count / severityTotal) * 100))}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Top versions</p>
            <div className="mt-2 space-y-2">
              {versionEntries.length === 0 && <p className="text-sm text-muted-foreground">No version data yet.</p>}
              {versionEntries.map(([version, count]) => (
                <div key={version} className="flex items-center justify-between rounded-lg border border-border/5 bg-background/5 px-3 py-2 text-sm">
                  <span className="font-mono text-xs uppercase">{version}</span>
                  <span className="text-foreground/80">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No recent frontend errors.</p>
      )}
    </div>
  );
}

function MCCapabilityProfile({ profile }: { profile: CapabilityDebugPayload | null }) {
  if (!profile) {
    return (
      <div className="aa-glass-card rounded-2xl p-4 text-sm text-muted-foreground">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Capability profile</p>
        <p>Plan sync pending…</p>
      </div>
    );
  }
  const enabled = Object.entries(profile.capabilities ?? {})
    .filter(([, allowed]) => Boolean(allowed))
    .map(([flag]) => flag);
  return (
    <div className="aa-glass-card rounded-2xl border-primary/40 p-4 text-sm text-foreground">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-primary">Capability profile</p>
          <h3 className="text-lg font-semibold text-foreground">Plan {planLabel(profile.tier)}</h3>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <p>Org plan: {planLabel(profile.organization_tier)}</p>
          <p>Credits: {profile.credits ?? 0}</p>
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <p className="text-xs text-muted-foreground">Credit expiration</p>
          <p className="text-sm text-foreground">
            {profile.credit_expires_at ? new Date(profile.credit_expires_at).toLocaleDateString() : "—"}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Limits</p>
          <p className="text-sm text-foreground">
            {profile.limits && "ai_task_costs" in profile.limits
              ? `AI tasks: ${JSON.stringify((profile.limits as Record<string, unknown>).ai_task_costs)}`
              : "—"}
          </p>
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Enabled: {enabled.slice(0, 8).join(", ") || "baseline"}
        {enabled.length > 8 ? ` +${enabled.length - 8} more` : ""}
      </p>
    </div>
  );
}

type OwnerTierToggleCardProps = {
  value: string;
  onValueChange: (tier: string) => void;
  onApply: () => Promise<void> | void;
  applying: boolean;
  currentTier: string;
};

function OwnerTierToggleCard({ value, onValueChange, onApply, applying, currentTier }: OwnerTierToggleCardProps) {
  return (
    <Card className="aa-glass-card rounded-2xl border-primary/40 text-foreground">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">Plan testing tools (Owner only)</CardTitle>
        <p className="text-sm text-muted-foreground">Switch your current session plan to test capability gates.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex-1 space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Select plan</p>
            <Select value={value} onValueChange={onValueChange}>
              <SelectTrigger className="w-full bg-background/40 text-foreground">
                <SelectValue placeholder="Choose plan" />
              </SelectTrigger>
              <SelectContent className="bg-background/40 text-foreground">
                {OWNER_TIER_OPTIONS.map((tier) => (
                  <SelectItem key={tier} value={tier}>
                    {tier === "OWNER" ? "Owner" : planLabel(tier)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            onClick={() => onApply()}
            disabled={applying || value === currentTier}
            className="w-full md:w-auto"
          >
            {applying ? "Applying…" : "Apply plan"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          This is an owner-only testing tool. Plan changes affect your current session only and reset when you reload.
        </p>
      </CardContent>
    </Card>
  );
}

type CapabilityState = {
  flag: string;
  allowed: boolean;
};

type BackendCapabilityState = {
  flag: string;
  allowed: boolean | null;
};

type PlanTestingPlaceholderProps = {
  reasons: string[];
};

type PlanTestingDebugPanelProps = {
  sessionRole?: string | null;
  sessionTier?: string | null;
  normalizedSessionPlan: PlanKeyExt;
  capabilityProfile: CapabilityDebugPayload | null;
  planCardVisible: boolean;
  planCardReasons: string[];
  clientCapabilityStates: CapabilityState[];
  backendCapabilityStates: BackendCapabilityState[];
  masterControlGateAllowed: boolean;
};

function PlanTestingPlaceholder({ reasons }: PlanTestingPlaceholderProps) {
  const blockers = reasons.length
    ? reasons
    : ["Owner session data is pending; visibility will update once the session resolves."];
  return (
    <Card className="rounded-2xl border border-rose-500/30 bg-rose-500/5 text-foreground">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">Plan testing tools hidden</CardTitle>
        <p className="text-sm text-muted-foreground">
          This owner-only testing card appears only when the session is recognized as an owner.
        </p>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <ul className="list-disc space-y-1 pl-5 text-[13px] text-rose-100">
          {blockers.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function PlanTestingDebugPanel({
  sessionRole,
  sessionTier,
  normalizedSessionPlan,
  capabilityProfile,
  planCardVisible,
  planCardReasons,
  clientCapabilityStates,
  backendCapabilityStates,
  masterControlGateAllowed,
}: PlanTestingDebugPanelProps) {
  const sessionPlanLabel = planLabel(sessionTier);
  const capabilityProfilePlanLabel = planLabel(capabilityProfile?.tier);
  const capabilityProfilePlanKey = normalizePlanKey(capabilityProfile?.tier);
  const capabilityProfileLoaded = Boolean(capabilityProfile);

  return (
    <Card className="rounded-2xl border border-border bg-background/60 text-foreground/80">
      <CardHeader className="p-0">
        <CardTitle className="text-base font-semibold text-foreground">Plan visibility diagnostics</CardTitle>
        <p className="text-xs text-muted-foreground">
          Tracks why the owner-only plan testing tools are visible or hidden for this session.
        </p>
      </CardHeader>
      <CardContent className="p-0 pt-3">
        <div className="grid gap-2 text-xs md:grid-cols-2">
          <div className="flex items-center justify-between text-foreground/70">
            <span>Session role</span>
            <span className="font-semibold text-foreground">{sessionRole ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between text-foreground/70">
            <span>Session tier</span>
            <span className="font-semibold text-foreground">{sessionTier ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between text-foreground/70">
            <span>Normalized plan key</span>
            <span className="font-semibold text-foreground">{normalizedSessionPlan}</span>
          </div>
          <div className="flex items-center justify-between text-foreground/70">
            <span>Plan label (session)</span>
            <span className="font-semibold text-foreground">{sessionPlanLabel}</span>
          </div>
          <div className="flex items-center justify-between text-foreground/70">
            <span>Plan label (capability profile)</span>
            <span className="font-semibold text-foreground">{capabilityProfilePlanLabel}</span>
          </div>
          <div className="flex items-center justify-between text-foreground/70">
            <span>Normalized backend plan key</span>
            <span className="font-semibold text-foreground">{capabilityProfilePlanKey}</span>
          </div>
          <div className="flex items-center justify-between text-foreground/70">
            <span>Master Control gate</span>
            <span
              className={`font-semibold ${masterControlGateAllowed ? "text-emerald-300" : "text-rose-400"}`}
            >
              {masterControlGateAllowed ? "pass" : "blocked"}
            </span>
          </div>
          <div className="flex items-center justify-between text-foreground/70">
            <span>Capability profile</span>
            <span className="font-semibold text-foreground/80">{capabilityProfileLoaded ? "loaded" : "loading"}</span>
          </div>
          <div className="flex items-center justify-between text-foreground/70">
            <span>Plan card status</span>
            <span className={`font-semibold ${planCardVisible ? "text-emerald-300" : "text-rose-400"}`}>
              {planCardVisible ? "visible" : "hidden"}
            </span>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Client capability map</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {clientCapabilityStates.map((state) => (
              <div
                key={state.flag}
                className="rounded-2xl border border-border bg-background/40 p-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
              >
                <span className="block text-[10px] text-muted-foreground">{state.flag}</span>
                <span className={`text-sm font-semibold ${state.allowed ? "text-emerald-300" : "text-rose-400"}`}>
                  {state.allowed ? "enabled" : "disabled"}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Backend capability map</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {backendCapabilityStates.map((state) => {
              const labelClass =
                state.allowed === null ? "text-primary" : state.allowed ? "text-emerald-300" : "text-rose-400";
              const labelText =
                state.allowed === null ? "unknown" : state.allowed ? "enabled" : "disabled";
              return (
                <div
                  key={`backend-${state.flag}`}
                  className="rounded-2xl border border-border bg-background/40 p-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                >
                  <span className="block text-[10px] text-muted-foreground">{state.flag}</span>
                  <span className={`text-sm font-semibold ${labelClass}`}>{labelText}</span>
                </div>
              );
            })}
          </div>
        </div>
        {!planCardVisible && planCardReasons.length > 0 && (
          <div className="mt-3 rounded-2xl border border-rose-500/30 bg-rose-500/5 p-3 text-[12px] text-rose-100">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-rose-200">
              Visibility blockers
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-4 leading-snug">
              {planCardReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
